import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  Plus, X, FloppyDisk, BookOpen, Trash, GearSix, ChatCircle,
  Target, CrosshairSimple, Trophy, Note, Clock, ListChecks,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL;

const APP_ICONS = {
  tip_menu: ListChecks,
  auto_greeter: ChatCircle,
  tip_goal: Target,
  wheel: CrosshairSimple,
  top_tippers: Trophy,
  notice: Note,
  countdown: Clock,
};

/**
 * Creator-side configurator for stream apps.
 * Props:
 *   streamId: string (optional — if set, syncs to live stream)
 *   initialApps: array (optional initial config)
 *   onSave: (apps) => void
 */
const StreamAppsConfigurator = ({ streamId, initialApps, onSave }) => {
  const [catalog, setCatalog] = useState([]);
  const [activeApps, setActiveApps] = useState(initialApps || []);
  const [presets, setPresets] = useState([]);
  const [presetName, setPresetName] = useState('');
  const [editingIdx, setEditingIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/stream-apps/catalog`, { withCredentials: true });
      setCatalog(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const loadPresets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/stream-apps/presets`, { withCredentials: true });
      setPresets(res.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadCatalog();
    loadPresets();
  }, [loadCatalog, loadPresets]);

  const addApp = (appKey) => {
    const meta = catalog.find(a => a.key === appKey);
    if (!meta) return;
    if (activeApps.find(a => a.app_key === appKey)) {
      toast.info(`${meta.name} already added`);
      return;
    }
    setActiveApps([...activeApps, { app_key: appKey, config: { ...meta.default_config } }]);
  };

  const removeApp = (idx) => {
    setActiveApps(activeApps.filter((_, i) => i !== idx));
    if (editingIdx === idx) setEditingIdx(null);
  };

  const updateConfig = (idx, config) => {
    setActiveApps(activeApps.map((a, i) => i === idx ? { ...a, config } : a));
  };

  const persist = async () => {
    setSaving(true);
    try {
      if (streamId) {
        await axios.put(`${API}/api/stream-apps/stream/${streamId}`,
          { apps: activeApps }, { withCredentials: true });
      }
      onSave?.(activeApps);
      toast.success('Stream apps saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const loadPreset = (preset) => {
    setActiveApps(preset.apps);
    setEditingIdx(null);
    toast.success(`Loaded preset "${preset.name}"`);
  };

  const saveAsPreset = async () => {
    if (!presetName.trim()) { toast.error('Enter a preset name'); return; }
    if (activeApps.length === 0) { toast.error('Add at least one app first'); return; }
    try {
      await axios.post(`${API}/api/stream-apps/presets`,
        { name: presetName, apps: activeApps }, { withCredentials: true });
      toast.success('Preset saved');
      setPresetName('');
      loadPresets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save preset failed');
    }
  };

  const deletePreset = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try {
      await axios.delete(`${API}/api/stream-apps/presets/${id}`, { withCredentials: true });
      loadPresets();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="stream-apps-configurator">
      {/* Presets bar */}
      {presets.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap p-2 bg-slate-900/50 rounded-xl">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-wider px-2">Presets:</span>
          {presets.map(p => (
            <div key={p.id} className="flex items-center gap-1 bg-slate-800 rounded-full pr-1">
              <button onClick={() => loadPreset(p)}
                className="px-3 py-1 text-sm text-slate-200 hover:text-white"
                data-testid={`preset-load-${p.id}`}>
                <BookOpen className="w-3 h-3 inline mr-1" /> {p.name}
              </button>
              <button onClick={() => deletePreset(p.id)} className="p-1 text-slate-600 hover:text-red-400">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Active apps */}
      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
          Active Apps ({activeApps.length})
        </h4>
        {activeApps.length === 0 ? (
          <p className="text-slate-600 text-sm">No apps added yet. Pick from the catalog below.</p>
        ) : (
          <div className="space-y-2">
            {activeApps.map((app, idx) => {
              const meta = catalog.find(c => c.key === app.app_key);
              const Icon = APP_ICONS[app.app_key] || GearSix;
              return (
                <div key={`${app.app_key}-${idx}`} className="bg-slate-900/70 rounded-xl border border-white/5"
                     data-testid={`active-app-${app.app_key}`}>
                  <div className="flex items-center justify-between p-3">
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-blue-400" />
                      <div>
                        <p className="text-white font-medium">{meta?.name || app.app_key}</p>
                        <p className="text-xs text-slate-500">{meta?.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setEditingIdx(editingIdx === idx ? null : idx)}
                        className="p-1.5 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg"
                        data-testid={`edit-app-${app.app_key}`}>
                        <GearSix className="w-4 h-4" />
                      </button>
                      <button onClick={() => removeApp(idx)}
                        className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
                        data-testid={`remove-app-${app.app_key}`}>
                        <Trash className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {editingIdx === idx && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
                      className="px-3 pb-3 border-t border-white/5">
                      <AppConfigEditor app={app} onChange={(cfg) => updateConfig(idx, cfg)} />
                    </motion.div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Catalog */}
      <div>
        <h4 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Add an App</h4>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {catalog.map(c => {
            const isAdded = activeApps.find(a => a.app_key === c.key);
            const Icon = APP_ICONS[c.key] || GearSix;
            return (
              <button key={c.key} onClick={() => addApp(c.key)} disabled={isAdded}
                className={`p-3 rounded-xl text-left flex items-start gap-2 transition-colors ${
                  isAdded ? 'bg-blue-500/10 border border-blue-500/20 opacity-50 cursor-not-allowed'
                          : 'bg-slate-800 hover:bg-slate-700 border border-transparent'
                }`}
                data-testid={`add-app-${c.key}`}>
                <Icon className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{c.name}</p>
                  <p className="text-xs text-slate-500 line-clamp-2">{c.description}</p>
                  {isAdded && <span className="text-[10px] text-blue-400 font-bold">ADDED</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Save row */}
      <div className="flex items-center gap-2 pt-3 border-t border-white/5">
        <Input value={presetName} onChange={e => setPresetName(e.target.value)}
          placeholder="Save current setup as preset…"
          className="flex-1 bg-slate-800 border-white/10 text-sm" data-testid="preset-name-input" />
        <button onClick={saveAsPreset}
          className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm flex items-center gap-1"
          data-testid="save-preset-btn">
          <BookOpen className="w-4 h-4" /> Save Preset
        </button>
        <button onClick={persist} disabled={saving}
          className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50 flex items-center gap-1"
          data-testid="save-apps-btn">
          <FloppyDisk className="w-4 h-4" /> {saving ? 'Saving…' : 'Apply'}
        </button>
      </div>
    </div>
  );
};

/** Per-app config editor */
const AppConfigEditor = ({ app, onChange }) => {
  const cfg = app.config || {};
  const set = (k, v) => onChange({ ...cfg, [k]: v });

  if (app.app_key === 'tip_menu') {
    const items = cfg.items || [];
    return (
      <div className="space-y-2 mt-3">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={cfg.title || ''} onChange={e => set('title', e.target.value)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
        <Label className="text-xs">Menu Items</Label>
        <div className="space-y-1">
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 items-center">
              <Input value={it.label} onChange={e => set('items', items.map((x, j) => j === i ? { ...x, label: e.target.value } : x))}
                placeholder="Action label" className="flex-1 bg-slate-800 border-white/10" />
              <Input type="number" min="1" value={it.amount}
                onChange={e => set('items', items.map((x, j) => j === i ? { ...x, amount: parseInt(e.target.value) || 1 } : x))}
                className="w-20 bg-slate-800 border-white/10" />
              <button type="button" onClick={() => set('items', items.filter((_, j) => j !== i))}
                className="p-1 text-slate-500 hover:text-red-400 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button type="button" onClick={() => set('items', [...items, { label: '', amount: 10 }])}
            className="text-xs px-2 py-1 bg-slate-800 text-slate-300 rounded hover:bg-slate-700 flex items-center gap-1">
            <Plus className="w-3 h-3" /> Add Item
          </button>
        </div>
      </div>
    );
  }

  if (app.app_key === 'auto_greeter') {
    return (
      <div className="mt-3">
        <Label className="text-xs">Welcome message ({"{name}"} = viewer name)</Label>
        <Textarea value={cfg.message || ''} onChange={e => set('message', e.target.value)}
          className="bg-slate-800 border-white/10 mt-1" />
      </div>
    );
  }

  if (app.app_key === 'tip_goal') {
    return (
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div>
          <Label className="text-xs">Label</Label>
          <Input value={cfg.label || ''} onChange={e => set('label', e.target.value)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs">Token Goal</Label>
          <Input type="number" min="1" value={cfg.amount || 0}
            onChange={e => set('amount', parseInt(e.target.value) || 0)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
      </div>
    );
  }

  if (app.app_key === 'wheel') {
    const slices = cfg.slices || [];
    return (
      <div className="space-y-2 mt-3">
        <div>
          <Label className="text-xs">Spin Cost (tokens)</Label>
          <Input type="number" min="1" value={cfg.spin_cost || 1}
            onChange={e => set('spin_cost', parseInt(e.target.value) || 1)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
        <Label className="text-xs">Slices</Label>
        {slices.map((s, i) => (
          <div key={i} className="flex gap-2">
            <Input value={s} onChange={e => set('slices', slices.map((x, j) => j === i ? e.target.value : x))}
              className="flex-1 bg-slate-800 border-white/10" />
            <button type="button" onClick={() => set('slices', slices.filter((_, j) => j !== i))}
              className="p-1 text-slate-500 hover:text-red-400 rounded">
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <button type="button" onClick={() => set('slices', [...slices, ''])}
          className="text-xs px-2 py-1 bg-slate-800 text-slate-300 rounded hover:bg-slate-700 flex items-center gap-1">
          <Plus className="w-3 h-3" /> Add Slice
        </button>
      </div>
    );
  }

  if (app.app_key === 'top_tippers') {
    return (
      <div className="grid grid-cols-2 gap-2 mt-3">
        <div>
          <Label className="text-xs">Show top</Label>
          <Input type="number" min="1" max="10" value={cfg.limit || 5}
            onChange={e => set('limit', parseInt(e.target.value) || 5)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
      </div>
    );
  }

  if (app.app_key === 'notice') {
    return (
      <div className="space-y-2 mt-3">
        <div>
          <Label className="text-xs">Title</Label>
          <Input value={cfg.title || ''} onChange={e => set('title', e.target.value)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs">Body</Label>
          <Textarea value={cfg.body || ''} onChange={e => set('body', e.target.value)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
      </div>
    );
  }

  if (app.app_key === 'countdown') {
    return (
      <div className="space-y-2 mt-3">
        <div>
          <Label className="text-xs">Label</Label>
          <Input value={cfg.label || ''} onChange={e => set('label', e.target.value)}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
        <div>
          <Label className="text-xs">Ends at (ISO datetime)</Label>
          <Input type="datetime-local" value={cfg.ends_at?.slice(0, 16) || ''}
            onChange={e => set('ends_at', e.target.value ? new Date(e.target.value).toISOString() : '')}
            className="bg-slate-800 border-white/10 mt-1" />
        </div>
      </div>
    );
  }

  return <p className="text-slate-500 text-xs mt-2">No config options.</p>;
};

export default StreamAppsConfigurator;
